import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { supabase } from '../lib/supabase';

export interface SystemYear {
  id: string;
  year: number;
  name: string;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  storage_used_mb: number;
  task_count: number;
  evidence_count: number;
  is_archived: boolean;
}

interface StorageInfo {
  usedMB: number;
  limitMB: number;
  usedPercent: number;
}

interface YearContextType {
  years: SystemYear[];
  activeYear: number;
  setActiveYear: (year: number) => Promise<void>;
  storageInfo: StorageInfo;
  refreshYears: () => Promise<void>;
  refreshStorage: () => Promise<void>;
  loading: boolean;
}

const YearContext = createContext<YearContextType | undefined>(undefined);

const STORAGE_LIMIT_MB = 1024; // 1 GB en plan gratuito

export function YearProvider({ children }: { children: ReactNode }) {
  const [years, setYears] = useState<SystemYear[]>([]);
  const [activeYear, setActiveYearState] = useState<number>(2026);
  const [storageInfo, setStorageInfo] = useState<StorageInfo>({
    usedMB: 0,
    limitMB: STORAGE_LIMIT_MB,
    usedPercent: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchYears = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('system_years')
        .select('*')
        .eq('is_archived', false)
        .order('year', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        setYears(data);
        const active = data.find((y) => y.is_active);
        if (active) {
          setActiveYearState(active.year);
          localStorage.setItem('sgpi_active_year', String(active.year));
        }
      }
    } catch (err) {
      console.error('Error fetching years:', err);
    }
  }, []);

  const fetchStorageInfo = useCallback(async () => {
    try {
      // Calcular storage total usado (todas las evidencias, no solo del año activo)
      const { data: evidences, error } = await supabase
        .from('evidences')
        .select('file_size');

      if (error) throw error;

      let usedMB = 0;
      if (evidences) {
        usedMB = evidences.reduce((sum, e) => {
          // file_size puede ser '2.5 MB', '500 KB', etc.
          const raw = String(e.file_size || '0');
          const num = parseFloat(raw);
          if (isNaN(num)) return sum;
          if (raw.toLowerCase().includes('kb')) return sum + num / 1024;
          if (raw.toLowerCase().includes('gb')) return sum + num * 1024;
          return sum + num; // asume MB
        }, 0);
      }

      setStorageInfo({
        usedMB: Math.round(usedMB * 100) / 100,
        limitMB: STORAGE_LIMIT_MB,
        usedPercent: Math.min(Math.round((usedMB / STORAGE_LIMIT_MB) * 100), 100),
      });
    } catch (err) {
      console.error('Error fetching storage:', err);
    }
  }, []);

  const setActiveYear = useCallback(
    async (year: number) => {
      try {
        // Desactivar todos los años activos
        const { data: activeYears } = await supabase
          .from('system_years')
          .select('id')
          .eq('is_active', true);

        if (activeYears && activeYears.length > 0) {
          for (const ay of activeYears) {
            await supabase.from('system_years').update({ is_active: false }).eq('id', ay.id);
          }
        }

        // Activar el seleccionado
        const { error } = await supabase.from('system_years').update({ is_active: true }).eq('year', year);

        if (error) throw error;

        setActiveYearState(year);
        localStorage.setItem('sgpi_active_year', String(year));
        await fetchYears();
        await fetchStorageInfo();
      } catch (err) {
        console.error('Error setting active year:', err);
      }
    },
    [fetchYears, fetchStorageInfo]
  );

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      await fetchYears();

      // Si hay año guardado en localStorage, usarlo
      const saved = localStorage.getItem('sgpi_active_year');
      if (saved) {
        const savedYear = parseInt(saved, 10);
        const exists = years.find((y) => y.year === savedYear);
        if (exists && !exists.is_active) {
          await setActiveYear(savedYear);
        }
      }

      await fetchStorageInfo();
      setLoading(false);
    };

    initialize();
  }, [fetchYears, fetchStorageInfo, setActiveYear]);

  // Recalcular storage cada 30 segundos
  useEffect(() => {
    const interval = setInterval(fetchStorageInfo, 30000);
    return () => clearInterval(interval);
  }, [fetchStorageInfo]);

  return (
    <YearContext.Provider
      value={{
        years,
        activeYear,
        setActiveYear,
        storageInfo,
        refreshYears: fetchYears,
        refreshStorage: fetchStorageInfo,
        loading,
      }}
    >
      {children}
    </YearContext.Provider>
  );
}

export function useYear() {
  const context = useContext(YearContext);
  if (context === undefined) {
    throw new Error('useYear must be used within a YearProvider');
  }
  return context;
}